<!-- Main Calendar Component -->
<div class="programari-calendar">
  <!-- Header -->
  <div class="calendar-header">
    <div class="header-title">
      <h2>Program Activități</h2>
      <p class="subtitle">Gestionați și vizualizați activitățile programate</p>
    </div>
    
    <div class="header-actions">
      <button mat-raised-button color="primary" (click)="scheduleNewActivity()">
        <mat-icon>add</mat-icon>
        Programează Activitate
      </button>
    </div>
  </div>

  <!-- Toolbar -->
  <div class="calendar-toolbar">
    <!-- View Selector -->
    <div class="view-selector">
      <mat-button-toggle-group [(value)]="currentView" (change)="applyFilters()">
        <mat-button-toggle *ngFor="let view of calendarViews" [value]="view.key">
          <mat-icon>{{ view.icon }}</mat-icon>
          {{ view.label }}
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>

    <!-- Navigation -->
    <div class="calendar-navigation">
      <button mat-icon-button (click)="navigatePrevious()" matTooltip="Anterior">
        <mat-icon>chevron_left</mat-icon>
      </button>

      <div class="current-period" [ngSwitch]="currentView">
        <span *ngSwitchCase="'month'" class="period-text">{{ getMonthYear() }}</span>
        <span *ngSwitchCase="'week'" class="period-text">{{ getWeekRange() }}</span>
        <span *ngSwitchCase="'day'" class="period-text">{{ getDayDate() }}</span>
        <span *ngSwitchDefault class="period-text">Agendă</span>
      </div>

      <button mat-icon-button (click)="navigateNext()" matTooltip="Următor">
        <mat-icon>chevron_right</mat-icon>
      </button>

      <button mat-stroked-button (click)="navigateToday()" class="today-btn">
        <mat-icon>today</mat-icon>
        Astăzi
      </button>
    </div>

    <!-- Export Actions -->
    <div class="export-actions">
      <button mat-icon-button [matMenuTriggerFor]="exportMenu" matTooltip="Export">
        <mat-icon>download</mat-icon>
      </button>
      <mat-menu #exportMenu="matMenu">
        <button mat-menu-item (click)="exportToPDF()">
          <mat-icon>picture_as_pdf</mat-icon>
          <span>Export PDF</span>
        </button>
        <button mat-menu-item (click)="exportToExcel()">
          <mat-icon>table_chart</mat-icon>
          <span>Export Excel</span>
        </button>
      </mat-menu>
    </div>
  </div>

  <!-- Filters -->
  <mat-expansion-panel class="filters-panel">
    <mat-expansion-panel-header>
      <mat-panel-title>
        <mat-icon>filter_list</mat-icon>
        Filtre și Căutare
      </mat-panel-title>
      <mat-panel-description>
        {{ filteredActivities.length }} din {{ activities.length }} activități afișate
      </mat-panel-description>
    </mat-expansion-panel-header>

    <div class="filters-content">
      <!-- Search -->
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Căutare</mat-label>
        <input matInput 
               [(ngModel)]="searchTerm" 
               (input)="onSearchChange()" 
               placeholder="Căutați după titlu, observații...">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      <!-- Status Filter -->
      <div class="filter-group">
        <h4>Status</h4>
        <div class="status-filters">
          <mat-checkbox 
            *ngFor="let status of statusOptions"
            [checked]="selectedStatuses.includes(status.key)"
            (change)="$event.checked ? selectedStatuses.push(status.key) : selectedStatuses.splice(selectedStatuses.indexOf(status.key), 1); onStatusFilterChange()">
            <span class="status-indicator" [style.background-color]="status.color"></span>
            {{ status.label }}
          </mat-checkbox>
        </div>
      </div>

      <!-- Activity Type Filter -->
      <div class="filter-group">
        <h4>Tipuri de Activități</h4>
        <div class="activity-type-filters">
          <mat-checkbox 
            *ngFor="let type of activityTypes"
            [checked]="selectedActivityTypes.includes(type.id)"
            (change)="$event.checked ? selectedActivityTypes.push(type.id) : selectedActivityTypes.splice(selectedActivityTypes.indexOf(type.id), 1); onActivityTypeFilterChange()">
            {{ type.name }}
          </mat-checkbox>
        </div>
      </div>
    </div>
  </mat-expansion-panel>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <mat-progress-spinner diameter="50"></mat-progress-spinner>
    <p>Se încarcă activitățile...</p>
  </div>

  <!-- Calendar Content -->
  <div class="calendar-content" *ngIf="!isLoading">
    
    <!-- Month View -->
    <div class="month-view" *ngIf="currentView === 'month'">
      <div class="month-grid">
        <!-- Week Day Headers -->
        <div class="weekday-header" *ngFor="let day of weekDaysShort">
          {{ day }}
        </div>
        
        <!-- Calendar Days - Simplified for demo -->
        <div class="calendar-day" *ngFor="let i of [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35]; let dayIndex = index">
          <div class="day-number">{{ dayIndex + 1 <= 31 ? dayIndex + 1 : '' }}</div>
          <div class="day-activities">
            <div class="activity-dot" 
                 *ngFor="let activity of getActivitiesForCurrentView().slice(0,3)"
                 [style.background-color]="getStatusColor(activity.status)"
                 [matTooltip]="activity.title"
                 (click)="viewActivityDetails(activity)">
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Week/Day/Agenda Views -->
    <div class="activities-list" *ngIf="currentView !== 'month'">
      <!-- Activity Cards -->
      <mat-card class="activity-card" 
                *ngFor="let activity of getActivitiesForCurrentView(); trackBy: trackByActivityId"
                (click)="viewActivityDetails(activity)">
        
        <mat-card-content>
          <div class="activity-header">
            <div class="activity-status">
              <span class="status-indicator" [style.background-color]="getStatusColor(activity.status)"></span>
              <span class="status-label">{{ getStatusLabel(activity.status) }}</span>
            </div>
            <div class="activity-time">
              <mat-icon>schedule</mat-icon>
              {{ formatTime(activity.start_time) }}
              <span *ngIf="activity.duration_minutes"> ({{ activity.duration_minutes }}min)</span>
            </div>
          </div>

          <div class="activity-main">
            <h3 class="activity-title">{{ activity.title }}</h3>
            
            <div class="activity-details">
              <div class="activity-type" *ngIf="activity.activity_type">
                <mat-icon>category</mat-icon>
                {{ activity.activity_type.name }}
              </div>
              
              <div class="activity-date" *ngIf="currentView === 'agenda'">
                <mat-icon>event</mat-icon>
                {{ formatDate(activity.scheduled_date) }}
              </div>
            </div>

            <div class="activity-notes" *ngIf="activity.notes">
              <mat-icon>notes</mat-icon>
              {{ activity.notes }}
            </div>
          </div>

          <div class="activity-actions" (click)="$event.stopPropagation()">
            <button mat-icon-button (click)="editActivity(activity, $event)" matTooltip="Editare">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button (click)="deleteActivity(activity, $event)" matTooltip="Ștergere" color="warn">
              <mat-icon>delete</mat-icon>
            </button>
            <button mat-icon-button [matMenuTriggerFor]="activityMenu" matTooltip="Mai multe opțiuni">
              <mat-icon>more_vert</mat-icon>
            </button>
            
            <mat-menu #activityMenu="matMenu">
              <button mat-menu-item (click)="editActivity(activity)">
                <mat-icon>edit</mat-icon>
                <span>Editare</span>
              </button>
              <button mat-menu-item>
                <mat-icon>content_copy</mat-icon>
                <span>Duplicare</span>
              </button>
              <button mat-menu-item>
                <mat-icon>schedule</mat-icon>
                <span>Reprogramare</span>
              </button>
              <mat-divider></mat-divider>
              <button mat-menu-item (click)="deleteActivity(activity)" color="warn">
                <mat-icon>delete</mat-icon>
                <span>Ștergere</span>
              </button>
            </mat-menu>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Empty State -->
      <div class="empty-state" *ngIf="getActivitiesForCurrentView().length === 0">
        <mat-icon class="empty-icon">event_busy</mat-icon>
        <h3>Nu există activități pentru perioada selectată</h3>
        <p>Programați o activitate nouă sau modificați filtrele de căutare.</p>
        <button mat-raised-button color="primary" (click)="scheduleNewActivity()">
          <mat-icon>add</mat-icon>
          Programează Prima Activitate
        </button>
      </div>
    </div>
  </div>

  <!-- Statistics Summary -->
  <mat-card class="statistics-card">
    <mat-card-header>
      <mat-card-title>Rezumat Statistici</mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <div class="statistics-grid">
        <div class="stat-item" *ngFor="let status of statusOptions">
          <div class="stat-value">{{ (activities | filter:'status':status.key).length }}</div>
          <div class="stat-label">
            <span class="status-indicator" [style.background-color]="status.color"></span>
            {{ status.label }}
          </div>
        </div>
      </div>
    </mat-card-content>
  </mat-card>
</div>